<template>
  <div class="relative">
    <button
      :id="buttonId"
      type="button"
      :class="[
        'transtion-colors duration-150 flex text-sm p-4',
        emptyMiddle
          ? dropdownOpen
            ? 'bg-transparent text-black'
            : 'bg-transparent text-black hover:bg-black hover:text-white'
          : dropdownOpen
            ? 'sm:bg-transparent sm:text-black bg-transparent text-black'
            : 'sm:bg-transparent sm:text-black bg-transparent text-black hover:bg-black hover:text-white'
      ]"
      :aria-expanded="dropdownOpen"
      aria-haspopup="true"
      @click="dropdownOpen = !dropdownOpen"
    >
      <span class="sr-only">Open main menu</span>
      <MenuIcon :class="['h-6 w-6 transition-colors duration-150', dropdownOpen ? 'hidden' : 'block']" />
      <XIcon :class="['h-6 w-6 transition-colors duration-150', dropdownOpen ? 'block' : 'hidden']" />
    </button>

    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="dropdownOpen"
        class="origin-top-right absolute right-0 mt-0 w-56 py-1 z-20 ring-1 ring-black ring-opacity-5 focus:outline-none bg-black text-white"
        role="menu"
        aria-orientation="vertical"
        :aria-labelledby="buttonId"
        tabindex="-1"
      >
        <router-link to="/" class="flex items-center px-4 py-4 text-base hover:underline" @click="closeMenu">
          <HomeIcon class="h-4 w-4 mr-2 shrink-0" />
          <span>Home</span>
        </router-link>
        <a
          v-for="link in links"
          :key="link?.id"
          :href="link.attributes.url"
          target="_blank"
          class="flex items-center px-4 py-4 text-base hover:underline border-t"
          @click="closeMenu"
        >
          <span>{{ link.attributes.primary_text }}</span>
        </a>
        <router-link
          v-if="customer?.attributes?.reseller?.is_active"
          to="/resellers"
          class="flex items-center px-4 py-4 text-base hover:underline border-t"
          @click="closeMenu"
        >
          <BriefcaseIcon class="h-4 w-4 mr-2 shrink-0" />
          <span>Reseller Hub</span>
        </router-link>
        <router-link
          to="/profile"
          class="flex items-center px-4 py-4 text-base hover:underline border-t"
          @click="closeMenu"
        >
          <UserIcon class="h-4 w-4 mr-2 shrink-0" />
          <span class="truncate" v-if="customer?.attributes?.display_user_name">
            {{ customer.attributes.display_user_name }}
          </span>
          <span v-else-if="customer?.attributes">My Account</span>
          <span v-else>Log in</span>
        </router-link>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { UserIcon, MenuIcon, XIcon, HomeIcon, BriefcaseIcon } from '@heroicons/vue/outline'
import mapRelationships from '@/utils/mapRelationships'

export default {
  name: 'MerchantNavMenu',
  components: { UserIcon, MenuIcon, XIcon, HomeIcon, BriefcaseIcon },

  props: {
    buttonId: {
      type: String,
      default: 'main-menu-button'
    },
    emptyMiddle: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const store = useStore()
    const dropdownOpen = ref(false)

    const modules = computed(() =>
      mapRelationships(store.state, store.getters.globalPage?.relationships?.page_modules?.data || []).sort(
        (a, b) => a.attributes.order - b.attributes.order
      )
    )
    const module = computed(() => modules.value?.find((x) => x.attributes.type === 'MenuPageModule'))
    const links = computed(() =>
      mapRelationships(store.state, module.value?.relationships?.content_elements?.data || [])
    )
    const customer = computed(() => store.getters.customer)

    function closeMenu() {
      dropdownOpen.value = false
    }

    function onScroll() {
      if (dropdownOpen.value) {
        closeMenu()
      }
    }

    onMounted(() => {
      document.addEventListener('scroll', onScroll, { passive: true })
    })

    onBeforeUnmount(() => {
      document.removeEventListener('scroll', onScroll)
    })

    return {
      dropdownOpen,
      links,
      customer,
      closeMenu
    }
  }
}
</script>
